import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  UserCircleIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  KeyIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'super_admin' | 'admin' | 'moderator' | 'support';
  permissions: {
    users: { read: boolean; write: boolean; delete: boolean };
    jobs: { read: boolean; write: boolean; delete: boolean };
    courses: { read: boolean; write: boolean; delete: boolean };
    analytics: { read: boolean; write: boolean };
    system: { read: boolean; write: boolean };
    admin_users: { read: boolean; write: boolean; delete: boolean };
  };
  isActive: boolean;
  lastLogin?: Date;
  createdAt: Date;
  createdBy: string;
  department?: string;
  notes?: string;
}

interface AdminActivity {
  id: string;
  adminId: string;
  adminName: string;
  action: string;
  resource: string;
  details: string;
  timestamp: Date;
  ipAddress: string;
}

const AdminManagement: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [activities, setActivities] = useState<AdminActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<'admins' | 'activity'>('admins');

  const [newAdmin, setNewAdmin] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'admin' as const,
    department: '',
    notes: '',
    permissions: {
      users: { read: true, write: false, delete: false },
      jobs: { read: true, write: false, delete: false },
      courses: { read: true, write: false, delete: false },
      analytics: { read: true, write: false },
      system: { read: false, write: false },
      admin_users: { read: false, write: false, delete: false }
    }
  });

  useEffect(() => {
    fetchAdmins();
    fetchActivities();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      
      const response = await adminApiService.getAdminUsers();
      if (response.success && response.data) {
        // Transform backend data to frontend format
        const backendAdmins = response.data.admins;
        const transformedAdmins: AdminUser[] = backendAdmins.map((admin: any) => ({
          id: admin.id,
          email: admin.email,
          firstName: admin.firstName,
          lastName: admin.lastName,
          role: admin.adminRole,
          permissions: admin.permissions || {
            users: { read: false, write: false, delete: false },
            jobs: { read: false, write: false, delete: false },
            courses: { read: false, write: false, delete: false },
            analytics: { read: false, write: false },
            system: { read: false, write: false },
            admin_users: { read: false, write: false, delete: false }
          },
          isActive: admin.isActive !== false,
          lastLogin: admin.lastActivity ? new Date(admin.lastActivity.seconds * 1000) : undefined,
          createdAt: admin.createdAt ? new Date(admin.createdAt.seconds * 1000) : new Date(),
          createdBy: admin.createdBy || 'system',
          department: admin.department || '',
          notes: admin.notes || ''
        }));
        
        setAdmins(transformedAdmins);
      } else {
        throw new Error('Failed to load admin users');
      }
    } catch (error: any) {
      console.error('Failed to fetch admins:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await adminApiService.getAdminActivity();
      if (response.success && response.data) {
        // Transform backend data to frontend format
        const backendActivities = response.data.activities;
        const transformedActivities: AdminActivity[] = backendActivities.map((activity: any) => ({
          id: activity.id,
          adminId: activity.adminId,
          adminName: activity.adminName || 'Unknown Admin',
          action: activity.action,
          resource: activity.resource,
          details: activity.details || `${activity.action} on ${activity.resource}`,
          timestamp: activity.timestamp ? new Date(activity.timestamp.seconds * 1000) : new Date(),
          ipAddress: activity.ipAddress || 'Unknown'
        }));
        
        setActivities(transformedActivities);
      }
    } catch (error: any) {
      console.error('Failed to fetch activities:', error);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      if (!newAdmin.email || !newAdmin.firstName || !newAdmin.lastName) {
        toast.error('Email, first name, and last name are required');
        return;
      }

      const response = await adminApiService.createAdminUser({
        email: newAdmin.email,
        firstName: newAdmin.firstName,
        lastName: newAdmin.lastName,
        adminRole: newAdmin.role,
        department: newAdmin.department,
        notes: newAdmin.notes
      });

      if (response.success) {
        toast.success('Admin user created successfully');
        setShowCreateAdmin(false);
        setNewAdmin({
          email: '',
          firstName: '',
          lastName: '',
          role: 'admin',
          department: '',
          notes: '',
          permissions: {
            users: { read: true, write: false, delete: false },
            jobs: { read: true, write: false, delete: false },
            courses: { read: true, write: false, delete: false },
            analytics: { read: true, write: false },
            system: { read: false, write: false },
            admin_users: { read: false, write: false, delete: false }
          }
        });
        fetchAdmins();
      } else {
        throw new Error(response.message || 'Failed to create admin user');
      }
    } catch (error: any) {
      console.error('Create admin error:', error);
      toast.error('Failed to create admin user: ' + (error.message || 'Unknown error'));
    }
  };

  const handleToggleAdminStatus = async (adminId: string, isActive: boolean) => {
    try {
      const response = await adminApiService.updateAdminUser(adminId, { isActive });
      if (response.success) {
        toast.success(`Admin ${isActive ? 'activated' : 'deactivated'} successfully`);
        fetchAdmins();
      } else {
        throw new Error(response.message || 'Failed to update admin status');
      }
    } catch (error: any) {
      console.error('Toggle admin status error:', error);
      toast.error(`Failed to ${isActive ? 'activate' : 'deactivate'} admin: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (window.confirm('Are you sure you want to delete this admin user? This will deactivate the account for audit purposes.')) {
      try {
        const response = await adminApiService.deleteAdminUser(adminId);
        if (response.success) {
          toast.success('Admin user deactivated successfully');
          fetchAdmins();
        } else {
          throw new Error(response.message || 'Failed to delete admin user');
        }
      } catch (error: any) {
        console.error('Delete admin error:', error);
        toast.error('Failed to delete admin user: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      super_admin: { color: 'bg-red-100 text-red-800', label: 'Super Admin' },
      admin: { color: 'bg-blue-100 text-blue-800', label: 'Admin' },
      moderator: { color: 'bg-green-100 text-green-800', label: 'Moderator' },
      support: { color: 'bg-yellow-100 text-yellow-800', label: 'Support' }
    };
    
    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.admin;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? (
          <>
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="h-3 w-3 mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Admin Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage administrator accounts, roles, and permissions
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowCreateAdmin(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <UserPlusIcon className="h-4 w-4 mr-2" />
            Add Admin User
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('admins')}
            className={`${
              activeTab === 'admins'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserCircleIcon className="h-4 w-4 mr-2" />
            Admin Users ({admins.length})
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`${
              activeTab === 'activity'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ClockIcon className="h-4 w-4 mr-2" />
            Recent Activity ({activities.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'admins' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {admins.map((admin) => (
              <li key={admin.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="flex-shrink-0 mr-4">
                        <UserCircleIcon className="h-10 w-10 text-gray-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <p className="text-sm font-medium text-gray-900">
                            {admin.firstName} {admin.lastName}
                          </p>
                          {getRoleBadge(admin.role)}
                          {getStatusBadge(admin.isActive)}
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-500 space-x-4 mb-2">
                          <span>{admin.email}</span>
                          {admin.department && (
                            <>
                              <span>•</span>
                              <span>{admin.department}</span>
                            </>
                          )}
                        </div>
                        
                        <div className="flex items-center text-xs text-gray-500 space-x-4">
                          <span>
                            Created: {new Date(admin.createdAt).toLocaleDateString()}
                          </span>
                          {admin.lastLogin && (
                            <>
                              <span>•</span>
                              <span>
                                Last login: {new Date(admin.lastLogin).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                        
                        {admin.notes && (
                          <p className="text-sm text-gray-600 mt-2">{admin.notes}</p>
                        )}
                        
                        {/* Permissions Summary */}
                        <div className="mt-2 flex flex-wrap gap-1">
                          {Object.entries(admin.permissions).map(([resource, perms]) => {
                            const hasAnyPerm = Object.values(perms).some(Boolean);
                            if (!hasAnyPerm) return null;
                            
                            return (
                              <span
                                key={resource}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                              >
                                {resource.replace('_', ' ')}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-2 flex-shrink-0 flex space-x-2">
                      <button
                        onClick={() => handleToggleAdminStatus(admin.id, !admin.isActive)}
                        className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          admin.isActive
                            ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                            : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                        }`}
                      >
                        {admin.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedAdmin(admin);
                          setShowEditAdmin(true);
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <PencilSquareIcon className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      
                      {admin.role !== 'super_admin' && (
                        <button
                          onClick={() => handleDeleteAdmin(admin.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {activities.map((activity) => (
              <li key={activity.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="flex-shrink-0 mr-4">
                        <ClockIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.action}
                          </p>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {activity.resource}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{activity.details}</p>
                        
                        <div className="flex items-center text-xs text-gray-500 space-x-4">
                          <span>By: {activity.adminName}</span>
                          <span>•</span>
                          <span>{new Date(activity.timestamp).toLocaleString()}</span>
                          <span>•</span>
                          <span>IP: {activity.ipAddress}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowCreateAdmin(false)}>
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Admin User</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={newAdmin.firstName}
                      onChange={(e) => setNewAdmin({...newAdmin, firstName: e.target.value})}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={newAdmin.lastName}
                      onChange={(e) => setNewAdmin({...newAdmin, lastName: e.target.value})}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={newAdmin.role}
                      onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value as any})}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="admin">Admin</option>
                      <option value="moderator">Moderator</option>
                      <option value="support">Support</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                    <input
                      type="text"
                      value={newAdmin.department}
                      onChange={(e) => setNewAdmin({...newAdmin, department: e.target.value})}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={2}
                    value={newAdmin.notes}
                    onChange={(e) => setNewAdmin({...newAdmin, notes: e.target.value})}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCreateAdmin(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAdmin}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Create Admin
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;